import { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '@/lib/session';
import {
  fetchReservations,
  createReservation,
  updateReservation,
  deleteReservation,
  nextToken,
  fetchTodayTokens,
  type Reservation,
  type Order,
} from '@/lib/data';

type Tab = 'reservations' | 'tokens';

const STATUS_STYLES: Record<Reservation['status'], string> = {
  booked: 'bg-blue-100 text-blue-700',
  seated: 'bg-primary-100 text-primary-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
  no_show: 'bg-background-200 text-foreground-500',
};

const emptyForm = {
  customerName: '',
  phone: '',
  partySize: 2,
  date: new Date().toISOString().slice(0, 10),
  time: '19:00',
  tableId: '',
  notes: '',
};

export default function ReservationsPage() {
  const restaurant = useRestaurant();
  const [tab, setTab] = useState<Tab>('reservations');
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [tokens, setTokens] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastToken, setLastToken] = useState<number | null>(null);

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2500); };
  const flashErr = (e: unknown) => {
    setError(e instanceof Error ? e.message : 'Something went wrong.');
    setTimeout(() => setError(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        fetchReservations(restaurant.id),
        fetchTodayTokens(restaurant.id),
      ]);
      setReservations(r);
      setTokens(t);
    } catch (e) {
      flashErr(e);
    } finally {
      setLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.customerName.trim()) { flashErr(new Error('Customer name is required.')); return; }
    setBusy(true);
    try {
      const reservedAt = new Date(`${form.date}T${form.time}`).toISOString();
      const created = await createReservation(restaurant.id, {
        customerName: form.customerName.trim(),
        phone: form.phone.trim() || null,
        partySize: Number(form.partySize) || 1,
        reservedAt,
        durationMins: 90,
        tableId: form.tableId.trim() || null,
        notes: form.notes.trim() || null,
      });
      setReservations((prev) => [...prev, created].sort((a, b) => a.reservedAt.localeCompare(b.reservedAt)));
      setModalOpen(false);
      setForm({ ...emptyForm });
      flash('Reservation added.');
    } catch (e) {
      flashErr(e);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (r: Reservation, status: Reservation['status']) => {
    try {
      await updateReservation(restaurant.id, r.id, { status });
      setReservations((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
    } catch (e) {
      flashErr(e);
    }
  };

  const remove = async (r: Reservation) => {
    if (!window.confirm(`Delete reservation for ${r.customerName}?`)) return;
    try {
      await deleteReservation(restaurant.id, r.id);
      setReservations((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      flashErr(e);
    }
  };

  const generateToken = async () => {
    try {
      const n = await nextToken(restaurant.id);
      setLastToken(n);
      flash(`Token #${n} allocated. Assign it to the next counter order.`);
    } catch (e) {
      flashErr(e);
    }
  };

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Reservations &amp; Tokens</h1>
          <p className="text-sm text-foreground-500">Manage table bookings and QSR pickup tokens.</p>
        </div>
        {tab === 'reservations' && (
          <button
            onClick={() => { setForm({ ...emptyForm }); setModalOpen(true); }}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i> New Reservation
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-background-200">
        {([['reservations', 'ri-calendar-check-line', 'Reservations'], ['tokens', 'ri-hashtag', 'Token Board']] as const).map(([k, icon, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
              tab === k ? 'border-primary-500 text-primary-600' : 'border-transparent text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${icon} mr-1.5`}></i>{label}
          </button>
        ))}
      </div>

      {toast && <div className="px-4 py-2 text-sm rounded-lg bg-green-100 text-green-700">{toast}</div>}
      {error && <div className="px-4 py-2 text-sm rounded-lg bg-red-100 text-red-700">{error}</div>}

      {loading ? (
        <p className="text-sm text-foreground-500">Loading…</p>
      ) : tab === 'reservations' ? (
        reservations.length === 0 ? (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-calendar-2-line text-4xl"></i>
            <p className="mt-2 text-sm">No reservations yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-foreground-600">
                  <th className="p-3 font-medium">When</th>
                  <th className="p-3 font-medium">Guest</th>
                  <th className="p-3 font-medium">Party</th>
                  <th className="p-3 font-medium">Table</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-t border-background-100">
                    <td className="p-3 text-foreground-800">{fmtTime(r.reservedAt)}</td>
                    <td className="p-3">
                      <div className="font-medium text-foreground-800">{r.customerName}</div>
                      {r.phone && <div className="text-xs text-foreground-500">{r.phone}</div>}
                    </td>
                    <td className="p-3 text-foreground-700">{r.partySize}</td>
                    <td className="p-3 text-foreground-700">{r.tableId || '—'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[r.status]}`}>
                        {r.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {r.status === 'booked' && (
                          <button onClick={() => setStatus(r, 'seated')} className="px-2 py-1 text-xs rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200 cursor-pointer">Seat</button>
                        )}
                        {(r.status === 'booked' || r.status === 'seated') && (
                          <>
                            <button onClick={() => setStatus(r, 'completed')} className="px-2 py-1 text-xs rounded-md bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer">Done</button>
                            <button onClick={() => setStatus(r, 'no_show')} className="px-2 py-1 text-xs rounded-md bg-background-200 text-foreground-600 hover:bg-background-300 cursor-pointer">No-show</button>
                            <button onClick={() => setStatus(r, 'cancelled')} className="px-2 py-1 text-xs rounded-md bg-red-100 text-red-600 hover:bg-red-200 cursor-pointer">Cancel</button>
                          </>
                        )}
                        <button onClick={() => remove(r)} className="px-2 py-1 text-xs rounded-md text-foreground-400 hover:text-red-600 cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={generateToken}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i> Generate next token
            </button>
            {lastToken !== null && (
              <span className="text-sm text-foreground-600">Latest allocated: <span className="font-bold text-primary-600">#{lastToken}</span></span>
            )}
            <button onClick={load} className="px-3 py-2 text-sm rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 cursor-pointer">
              <i className="ri-refresh-line mr-1.5"></i> Refresh
            </button>
          </div>

          {tokens.length === 0 ? (
            <div className="text-center py-16 text-foreground-400">
              <i className="ri-hashtag text-4xl"></i>
              <p className="mt-2 text-sm">No tokens issued today. Tokens appear here once a counter order is assigned a number.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {tokens.map((o) => (
                <div key={o.id} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                  <div className="text-2xl font-bold text-primary-600">#{o.tokenNumber}</div>
                  <div className="text-xs text-foreground-500 mt-1 truncate">{o.customer}</div>
                  <div className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${o.status === 'ready' ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'}`}>
                    {o.status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-background-50 rounded-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-foreground-900">New Reservation</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 block">
                <span className="text-sm font-medium text-foreground-700">Guest name</span>
                <input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-foreground-700">Phone</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-foreground-700">Party size</span>
                <input type="number" min={1} value={form.partySize} onChange={(e) => setForm({ ...form, partySize: Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-foreground-700">Date</span>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-foreground-700">Time</span>
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <label className="col-span-2 block">
                <span className="text-sm font-medium text-foreground-700">Table (optional)</span>
                <input value={form.tableId} onChange={(e) => setForm({ ...form, tableId: e.target.value })} placeholder="e.g. T4"
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
              <label className="col-span-2 block">
                <span className="text-sm font-medium text-foreground-700">Notes</span>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                  className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 cursor-pointer">Cancel</button>
              <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-50">
                {busy ? 'Saving…' : 'Add Reservation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
